import React, { useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';

import { ReviewContextualNudge } from './ReviewContextualNudge.tsx';
import { useDetectPageWordCount } from './useDetectPageWordCount.ts';

const WORD_COUNT_THRESHOLD = 500;

export type ReviewContextualNudgeContext = {
	contentId: string;
	/** The live editor document. The nudge only shows where it exists, which is edit mode. */
	getAdfDocument?: () => Promise<DocNode | null>;
};

type ReviewContextualNudgeEligibilityProps = {
	context?: ReviewContextualNudgeContext;
	onGetSuggestions?: () => void;
	onShown?: () => void;
	onHidden?: () => void;
	subscribeToTargetClick?: (listener: () => void) => () => void;
};

const WordCountEligibleContextualNudge = ({
	context,
	getAdfDocument,
	onGetSuggestions,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: Pick<
	ReviewContextualNudgeEligibilityProps,
	'onGetSuggestions' | 'onShown' | 'onHidden' | 'subscribeToTargetClick'
> & {
	context: ReviewContextualNudgeContext;
	getAdfDocument: () => Promise<DocNode | null>;
}) => {
	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();
	const subscribeToDocumentChange = useCallback(
		(listener: () => void) => {
			// The context hands the listener the editor view, which the nudge doesn't need.
			const handleDocChange = () => listener();
			subscribeDocChange(handleDocChange);
			return () => unsubscribeDocChange(handleDocChange);
		},
		[subscribeDocChange, unsubscribeDocChange],
	);

	// Reads the document and listens to the editor.
	const isOverWordCount = useDetectPageWordCount({
		contentId: context.contentId,
		threshold: WORD_COUNT_THRESHOLD,
		getAdfDocument,
		subscribeToDocumentChange,
	});

	if (!isOverWordCount) {
		return null;
	}

	return (
		<ReviewContextualNudge
			onGetSuggestions={onGetSuggestions}
			onShown={onShown}
			onHidden={onHidden}
			subscribeToTargetClick={subscribeToTargetClick}
		/>
	);
};

/**
 * Mounts the contextual nudge only for long pages in the editor. Rendering the Post Office message
 * records an exposure, so anyone who is ineligible must never reach it. The word count reads the
 * document and listens to editor changes, so it only runs once the editor is available.
 */
export const ReviewContextualNudgeEligibility = ({
	context,
	onGetSuggestions,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: ReviewContextualNudgeEligibilityProps): React.JSX.Element | null => {
	// Without the live editor document (view mode, published pages) there is nothing to count. The
	// editor is only open for users who can edit, so there is no separate permission check.
	const getAdfDocument = context?.getAdfDocument;
	if (!context?.contentId || !getAdfDocument) {
		return null;
	}

	return (
		<WordCountEligibleContextualNudge
			context={context}
			getAdfDocument={getAdfDocument}
			onGetSuggestions={onGetSuggestions}
			onShown={onShown}
			onHidden={onHidden}
			subscribeToTargetClick={subscribeToTargetClick}
		/>
	);
};
